import { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import * as localStorageUtils from '@/context/utils/localStorage';
import { logger } from '@/utils/logger';

// Configuration optimisée du QueryClient
export const createOptimizedQueryClient = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Cache plus long pour réduire les requêtes réseau
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
        // Retry optimisé
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        retry: (failureCount, error: any) => {
          // Ne pas retry sur les erreurs 404 ou 401
          if (error?.status === 404 || error?.status === 401) return false;
          return failureCount < 2; // Maximum 2 retries
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Optimisations réseau
        refetchOnWindowFocus: false, // Évite les requêtes inutiles
        refetchOnReconnect: 'always',
        refetchOnMount: true,
      },
      mutations: {
        retry: 1,
        retryDelay: 1000,
      },
    },
  });

  return queryClient;
};

// Pre-fetch intelligent des données critiques
export const prefetchCriticalData = async (queryClient: QueryClient) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) return;

    // Pre-fetch parallèle des données essentielles
    await Promise.allSettled([
      // Profil utilisateur via vue sécurisée
      queryClient.prefetchQuery({
        queryKey: ['user-profile', session.user.id],
        queryFn: async () => {
          const { data } = await supabase
            .from('my_profile_private')
            .select('*')
            .single();
          return data;
        },
        staleTime: 10 * 60 * 1000,
      }),
      
      // Éleveurs favoris
      queryClient.prefetchQuery({
        queryKey: ['breeder-favorites', session.user.id],
        queryFn: async () => {
          const { data } = await supabase
            .from('breeder_favorites')
            .select('breeder_id, breeders(*)')
            .eq('user_id', session.user.id)
            .limit(10);
          return data;
        },
        staleTime: 5 * 60 * 1000,
      }),
      
      // Pets locaux (localStorage)
      queryClient.prefetchQuery({
        queryKey: ['user-pets'],
        queryFn: async () => {
          return localStorageUtils.loadPets?.() || [];
        },
        staleTime: 2 * 60 * 1000,
      }),
    ]);
  } catch (error) {
    logger.warn('Pre-fetch failed:', error);
  }
};